/**
 * Form validation utilities
 * Provides validation functions for authentication forms
 */

/**
 * Email validation regex
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Password strength requirements
 */
interface PasswordRequirements {
  minLength: number
  hasUppercase: boolean
  hasLowercase: boolean
  hasNumber: boolean
  hasSpecialChar: boolean
}

/**
 * Validate email format
 */
export const validateEmail = (email: string): boolean => {
  return EMAIL_REGEX.test(email.trim())
}

/**
 * Validate password strength
 */
export const validatePasswordStrength = (
  password: string
): {
  isValid: boolean
  requirements: PasswordRequirements
  errors: string[]
} => {
  const requirements: PasswordRequirements = {
    minLength: password.length,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  }

  const errors: string[] = []

  if (!requirements.minLength) {
    errors.push('Password must be at least 8 characters long')
  }
  if (!requirements.hasUppercase) {
    errors.push('Password must contain at least one uppercase letter')
  }
  if (!requirements.hasLowercase) {
    errors.push('Password must contain at least one lowercase letter')
  }
  if (!requirements.hasNumber) {
    errors.push('Password must contain at least one number')
  }
  if (!requirements.hasSpecialChar) {
    errors.push('Password must contain at least one special character')
  }

  return {
    isValid: errors.length === 0,
    requirements,
    errors,
  }
}

/**
 * Validate phone number format
 */
export const validatePhoneNumber = (phone: string): boolean => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')
  // Check if it's between 10-15 digits (international format)
  return digits.length >= 10 && digits.length <= 15
}

/**
 * Validate company name
 */
export const validateCompanyName = (name: string): boolean => {
  return name.trim().length >= 2 && name.trim().length <= 255
}

/**
 * Validate name fields (first name, last name)
 */
export const validateName = (name: string): boolean => {
  return name.trim().length >= 1 && name.trim().length <= 30
}

/**
 * Validate API key format
 */
export const validateAPIKey = (apiKey: string): boolean => {
  // API keys should start with 'pb_' and be 40+ characters
  return apiKey.startsWith('pb_') && apiKey.length >= 40
}

/**
 * Validate secret key format
 */
export const validateSecretKey = (secretKey: string): boolean => {
  // Secret keys should be 64 characters
  return secretKey.length === 64
}

/**
 * Validate login form data
 */
export const validateLoginForm = (data: {
  email: string
  password: string
}): {
  isValid: boolean
  errors: Record<string, string>
} => {
  const errors: Record<string, string> = {}

  if (!data.email.trim()) {
    errors.email = 'Email is required'
  } else if (!validateEmail(data.email)) {
    errors.email = 'Please enter a valid email address'
  }

  if (!data.password) {
    errors.password = 'Password is required'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Validate registration form data
 */
export const validateRegisterForm = (data: {
  email: string
  password: string
  first_name: string
  last_name: string
  company_name?: string
  phone_number?: string
}): {
  isValid: boolean
  errors: Record<string, string>
} => {
  const errors: Record<string, string> = {}

  // Email validation
  if (!data.email.trim()) {
    errors.email = 'Email is required'
  } else if (!validateEmail(data.email)) {
    errors.email = 'Please enter a valid email address'
  }

  // Password validation
  if (!data.password) {
    errors.password = 'Password is required'
  } else {
    const passwordValidation = validatePasswordStrength(data.password)
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.errors.join(', ')
    }
  }

  // Name validation
  if (!data.first_name.trim()) {
    errors.first_name = 'First name is required'
  } else if (!validateName(data.first_name)) {
    errors.first_name = 'First name must be between 1-30 characters'
  }

  if (!data.last_name.trim()) {
    errors.last_name = 'Last name is required'
  } else if (!validateName(data.last_name)) {
    errors.last_name = 'Last name must be between 1-30 characters'
  }

  // Optional fields validation
  if (data.company_name && !validateCompanyName(data.company_name)) {
    errors.company_name = 'Company name must be between 2-255 characters'
  }

  if (data.phone_number && !validatePhoneNumber(data.phone_number)) {
    errors.phone_number = 'Please enter a valid phone number'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Validate merchant registration form data
 */
export const validateMerchantRegisterForm = (data: {
  email: string
  password: string
  confirm_password: string
  first_name: string
  last_name: string
  company_name: string
  phone_number?: string
  website_url?: string
  terms_accepted: boolean
}): {
  isValid: boolean
  errors: Record<string, string>
} => {
  const errors: Record<string, string> = {}

  // Email validation
  if (!data.email.trim()) {
    errors.email = 'Email is required'
  } else if (!validateEmail(data.email)) {
    errors.email = 'Please enter a valid email address'
  }

  // Password validation
  if (!data.password) {
    errors.password = 'Password is required'
  } else {
    const passwordValidation = validatePasswordStrength(data.password)
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.errors.join(', ')
    }
  }

  // Password confirmation
  if (data.password !== data.confirm_password) {
    errors.confirm_password = 'Passwords do not match'
  }

  // Name validation
  if (!data.first_name.trim()) {
    errors.first_name = 'First name is required'
  } else if (!validateName(data.first_name)) {
    errors.first_name = 'First name must be between 1-30 characters'
  }

  if (!data.last_name.trim()) {
    errors.last_name = 'Last name is required'
  } else if (!validateName(data.last_name)) {
    errors.last_name = 'Last name must be between 1-30 characters'
  }

  // Company name validation (required for merchants)
  if (!data.company_name.trim()) {
    errors.company_name = 'Company name is required'
  } else if (!validateCompanyName(data.company_name)) {
    errors.company_name = 'Company name must be between 2-255 characters'
  }

  // Optional fields validation
  if (data.phone_number && !validatePhoneNumber(data.phone_number)) {
    errors.phone_number = 'Please enter a valid phone number'
  }

  if (data.website_url && data.website_url.trim()) {
    try {
      new URL(data.website_url)
    } catch {
      errors.website_url = 'Please enter a valid website URL'
    }
  }

  // Terms acceptance validation
  if (!data.terms_accepted) {
    errors.terms_accepted = 'You must accept the terms and conditions'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Validate API key login form data
 */
export const validateAPIKeyForm = (data: {
  api_key: string
  secret_key?: string
}): {
  isValid: boolean
  errors: Record<string, string>
} => {
  const errors: Record<string, string> = {}

  if (!data.api_key.trim()) {
    errors.api_key = 'API key is required'
  } else if (!validateAPIKey(data.api_key)) {
    errors.api_key = 'API key must start with "pb_" and be at least 40 characters'
  }

  if (data.secret_key && !validateSecretKey(data.secret_key)) {
    errors.secret_key = 'Secret key must be exactly 64 characters'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Validate forgot password form data
 */
export const validateForgotPasswordForm = (data: {
  email: string
}): {
  isValid: boolean
  errors: Record<string, string>
} => {
  const errors: Record<string, string> = {}

  if (!data.email.trim()) {
    errors.email = 'Email is required'
  } else if (!validateEmail(data.email)) {
    errors.email = 'Please enter a valid email address'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Validate reset password form data
 */
export const validateResetPasswordForm = (data: {
  token: string
  password: string
}): {
  isValid: boolean
  errors: Record<string, string>
} => {
  const errors: Record<string, string> = {}

  if (!data.token.trim()) {
    errors.token = 'Reset token is required'
  }

  if (!data.password) {
    errors.password = 'Password is required'
  } else {
    const passwordValidation = validatePasswordStrength(data.password)
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.errors.join(', ')
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Validate change password form data
 */
export const validateChangePasswordForm = (data: {
  current_password: string
  new_password: string
}): {
  isValid: boolean
  errors: Record<string, string>
} => {
  const errors: Record<string, string> = {}

  if (!data.current_password) {
    errors.current_password = 'Current password is required'
  }

  if (!data.new_password) {
    errors.new_password = 'New password is required'
  } else {
    const passwordValidation = validatePasswordStrength(data.new_password)
    if (!passwordValidation.isValid) {
      errors.new_password = passwordValidation.errors.join(', ')
    }
  }

  if (data.current_password && data.new_password && data.current_password === data.new_password) {
    errors.new_password = 'New password must be different from current password'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Validate profile update form data
 */
export const validateProfileForm = (data: {
  first_name?: string
  last_name?: string
  company_name?: string
  phone_number?: string
  address?: string
}): {
  isValid: boolean
  errors: Record<string, string>
} => {
  const errors: Record<string, string> = {}

  if (data.first_name !== undefined) {
    if (!data.first_name.trim()) {
      errors.first_name = 'First name is required'
    } else if (!validateName(data.first_name)) {
      errors.first_name = 'First name must be between 1-30 characters'
    }
  }

  if (data.last_name !== undefined) {
    if (!data.last_name.trim()) {
      errors.last_name = 'Last name is required'
    } else if (!validateName(data.last_name)) {
      errors.last_name = 'Last name must be between 1-30 characters'
    }
  }

  if (
    data.company_name !== undefined &&
    data.company_name &&
    !validateCompanyName(data.company_name)
  ) {
    errors.company_name = 'Company name must be between 2-255 characters'
  }

  if (
    data.phone_number !== undefined &&
    data.phone_number &&
    !validatePhoneNumber(data.phone_number)
  ) {
    errors.phone_number = 'Please enter a valid phone number'
  }

  if (data.address !== undefined && data.address && data.address.trim().length > 500) {
    errors.address = 'Address must be less than 500 characters'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}
